import { evaluateCondition } from "../dsl/expression.js";
import { isMixedCategoryPlan } from "./paginate.js";
/**
 * Picks the layout for one page.
 *
 * Constraints filter, then scoring ranks. The scoring exists to stop a book
 * from using its single best-fitting layout on every page: a template applied
 * to twenty categories should look like a designed book, not twenty copies of
 * the same page, and that only happens if repetition costs something.
 */
export function selectBlueprint(template, context, prng) {
    const { plan, profile, side, history } = context;
    const rejected = [];
    const candidates = template.blueprints.filter((blueprint) => {
        if (blueprint.group !== plan.group)
            return false;
        const rejection = violatedConstraint(blueprint, context);
        if (rejection) {
            rejected.push({ blueprintId: blueprint.id, reason: rejection });
            return false;
        }
        return true;
    });
    if (candidates.length === 0) {
        return { blueprint: null, score: 0, reasoning: [], rejected };
    }
    const scored = candidates.map((blueprint) => {
        const reasoning = [];
        let score = 0;
        // Author preference, normalised so a weight of 100 cannot swamp everything.
        const authorWeight = blueprint.weight / 10;
        score += authorWeight;
        reasoning.push(`author weight +${authorWeight.toFixed(1)}`);
        // Capacity fit: how close the page's item count is to what this layout was
        // designed to hold.
        if (blueprint.capacity && plan.items.length > 0) {
            const { min, preferred, max } = blueprint.capacity;
            const distance = Math.abs(plan.items.length - preferred);
            const span = Math.max(preferred - min, max - preferred, 1);
            const fit = Math.max(0, 1 - distance / span) * 6;
            score += fit;
            reasoning.push(`capacity fit +${fit.toFixed(1)}`);
        }
        // Photo coverage: a grid of cards with no photos is worse than a list.
        if (profile) {
            const wantsPhotos = blueprint.constraints.minImageCoverage ?? 0;
            const coverageBonus = wantsPhotos > 0 ? profile.imageCoverage * 4 : 0;
            if (coverageBonus) {
                score += coverageBonus;
                reasoning.push(`photo coverage +${coverageBonus.toFixed(1)}`);
            }
        }
        // Variety: the same variant appearing again soon is penalised sharply, and
        // the penalty decays with distance.
        const lastUse = history.lastIndexOf(blueprint.variant);
        if (lastUse !== -1) {
            const distance = history.length - lastUse;
            const penalty = Math.max(0, (template.flow.varietyWindow - distance + 1) * 3);
            score -= penalty;
            if (penalty > 0)
                reasoning.push(`repeat penalty -${penalty.toFixed(1)}`);
        }
        // Asymmetry: templates that declare a side preference get a nudge towards
        // alternating spreads rather than two identical facing pages.
        if (blueprint.constraints.side && blueprint.constraints.side === side) {
            score += 1.5;
            reasoning.push('side match +1.5');
        }
        // A small deterministic jitter breaks ties differently per instance, which
        // is the difference between "two restaurants, same book" and "same
        // template, different books".
        const jitter = prng.next() * 1.2;
        score += jitter;
        return { blueprint, score, reasoning };
    });
    scored.sort((a, b) => b.score - a.score);
    const winner = scored[0];
    return {
        blueprint: winner.blueprint,
        score: winner.score,
        reasoning: winner.reasoning,
        rejected,
    };
}
function violatedConstraint(blueprint, context) {
    const { constraints } = blueprint;
    const { plan, profile, side } = context;
    const itemCount = plan.items.length;
    if (constraints.minItems !== undefined && itemCount < constraints.minItems) {
        return `needs at least ${constraints.minItems} items, page has ${itemCount}`;
    }
    if (constraints.maxItems !== undefined && itemCount > constraints.maxItems) {
        return `holds at most ${constraints.maxItems} items, page has ${itemCount}`;
    }
    if (profile) {
        if (constraints.minImageCoverage !== undefined &&
            pageImageCoverage(plan) < constraints.minImageCoverage) {
            return `needs ${Math.round(constraints.minImageCoverage * 100)}% photo coverage, page has ${Math.round(pageImageCoverage(plan) * 100)}%`;
        }
        if (constraints.maxImageCoverage !== undefined &&
            pageImageCoverage(plan) > constraints.maxImageCoverage) {
            return 'too many photos for this layout';
        }
        if (constraints.maxDescriptionLength !== undefined &&
            profile.maxDescriptionLength > constraints.maxDescriptionLength) {
            return `descriptions run to ${profile.maxDescriptionLength} characters, layout allows ${constraints.maxDescriptionLength}`;
        }
        if (constraints.maxNameLength !== undefined &&
            profile.maxNameLength > constraints.maxNameLength) {
            return `dish names run to ${profile.maxNameLength} characters, layout allows ${constraints.maxNameLength}`;
        }
    }
    if (constraints.requiresCategoryImage && !plan.category?.image) {
        return 'requires a category photo';
    }
    if (constraints.firstPageOfCategoryOnly &&
        (plan.pageInCategory !== 0 || isMixedCategoryPlan(plan))) {
        return 'only valid on the first page of a category';
    }
    if (constraints.side && constraints.side !== 'any' && constraints.side !== side) {
        return `only valid on the ${constraints.side} page of a spread`;
    }
    if (constraints.allowedWhen) {
        const scope = {
            page: { index: context.pageIndex, side, itemCount },
            category: plan.category
                ? { ...plan.category, itemCount: plan.category.items.length }
                : undefined,
            item: plan.spotlightItem,
            profile,
        };
        if (!evaluateCondition(constraints.allowedWhen, scope)) {
            return 'custom condition not met';
        }
    }
    return null;
}
function pageImageCoverage(plan) {
    if (plan.items.length === 0)
        return 0;
    return plan.items.filter((item) => item.image).length / plan.items.length;
}
//# sourceMappingURL=select-blueprint.js.map